import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ArrowRight, TrendingUp, TrendingDown, Minus, RefreshCw, Award, Target } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CATEGORIES } from "@/lib/data";
import { levelText } from "@/lib/scoring";

function diffIcon(delta) {
  if (delta > 1) return <TrendingUp size={14} className="text-emerald-400" />;
  if (delta < -1) return <TrendingDown size={14} className="text-rose-400" />;
  return <Minus size={14} className="text-neutral-500" />;
}

export default function MyPanel() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/auth/submissions").then((r) => setSubs(r.data.submissions || []))
      .finally(() => setLoading(false));
  }, []);

  const monthly = useMemo(() => {
    const groups = {};
    [...subs].reverse().forEach((s) => {
      const d = new Date(s.completed_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!groups[key]) groups[key] = { key, sum: 0, count: 0, top: 0 };
      groups[key].sum += s.general_score;
      groups[key].top = Math.max(groups[key].top, s.general_score);
      groups[key].count += 1;
    });
    const TR_MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
    return Object.values(groups).sort((a, b) => a.key.localeCompare(b.key)).map((g) => {
      const [y, m] = g.key.split("-");
      return { ...g, label: `${TR_MONTHS[Number(m) - 1]} ${y.slice(2)}`, avg: Math.round(g.sum / g.count) };
    });
  }, [subs]);

  if (loading) return <div className="text-center py-24 text-neutral-500">Yükleniyor...</div>;

  const latest = subs[0];
  const previous = subs[1];

  const monthDelta = monthly.length >= 2 ? monthly[monthly.length - 1].avg - monthly[monthly.length - 2].avg : null;
  const challengeMet = monthDelta !== null && monthDelta >= 2;

  if (!latest) {
    return (
      <div data-testid="panel-empty">
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight h-gradient">
          {user?.first_name ? `${user.first_name}, ` : ""}henüz testi çözmedin
        </h1>
        <p className="mt-5 text-neutral-400 max-w-xl">50 soruluk Life 101 testini tamamla, kişisel skorunu ve AI raporunu hemen al.</p>
        <button onClick={() => nav("/test")} className="btn-primary mt-8 inline-flex items-center gap-2" data-testid="panel-start-test">
          Testi Başlat <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  const radarData = CATEGORIES.map((c) => ({
    subject: c.label,
    son: latest.scores[c.key] ?? 0,
    onceki: previous?.scores?.[c.key] ?? 0,
  }));

  const fmt = (d) => new Date(d).toLocaleString("tr-TR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const generalDelta = previous ? Math.round(latest.general_score - previous.general_score) : 0;

  return (
    <div data-testid="panel-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Panelim</div>
          <h1 className="mt-2 text-5xl md:text-6xl font-semibold tracking-tight h-gradient">
            {user?.first_name ? `${user.first_name}'in Panosu` : "Panom"}
          </h1>
          <p className="mt-3 text-neutral-400">Son skorun, geçmiş testlerin ve gelişim grafiği.</p>
        </div>
        <button onClick={() => nav("/test")} className="btn-primary inline-flex items-center gap-2" data-testid="panel-retake">
          <RefreshCw size={16} /> Testi Tekrar Çöz
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-10">
        <div className="lg:col-span-4 glass p-7" data-testid="panel-latest-card">
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Son Skorun</div>
          <div className="mt-3 text-7xl font-bold h-gradient leading-none">
            {latest.general_score}<span className="text-2xl text-neutral-500 align-top ml-1">/100</span>
          </div>
          <div className="mt-3 text-neutral-400 text-sm">{levelText(latest.general_score)}</div>
          {previous && (
            <div className="mt-5 flex items-center gap-2 text-sm">
              {diffIcon(generalDelta)}
              <span className="text-neutral-300">
                {generalDelta === 0 ? "Önceki testle aynı seviyedesin" : generalDelta > 0 ? `Önceki teste göre +${generalDelta} puan` : `Önceki teste göre ${generalDelta} puan`}
              </span>
            </div>
          )}
          <div className="mt-6 text-xs text-neutral-500">{fmt(latest.completed_at)}</div>

          <div className="mt-6 flex gap-2">
            <Link to="/skor" className="btn-secondary text-xs">Skor Sayfası</Link>
            <Link to="/rapor" className="btn-secondary text-xs">AI Rapor</Link>
          </div>
        </div>

        <div className="lg:col-span-8 glass p-6" data-testid="panel-compare-chart">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-neutral-300">Son vs Önceki Test</div>
            {previous ? (
              <div className="flex items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-2"><span className="w-3 h-1 bg-white rounded-full"></span>Son</span>
                <span className="inline-flex items-center gap-2 text-neutral-400"><span className="w-3 h-1 bg-neutral-500 rounded-full"></span>Önceki</span>
              </div>
            ) : <span className="text-xs text-neutral-500">İlk testin — karşılaştırma için tekrar çöz</span>}
          </div>
          <ResponsiveContainer width="100%" height={340}>
            <RadarChart data={radarData} outerRadius="78%">
              <PolarGrid stroke="#1f1f1f" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: "#a3a3a3", fontSize: 11 }} />
              {previous && <Radar dataKey="onceki" stroke="#737373" fill="rgba(115,115,115,0.10)" strokeWidth={1} />}
              <Radar dataKey="son" stroke="#ffffff" fill="rgba(255,255,255,0.18)" strokeWidth={1.5} dot={{ fill: "#fff", r: 3 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-category history */}
      <div className="glass mt-6 p-6" data-testid="panel-categories">
        <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-5">Alanlara Göre Skorların</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {CATEGORIES.map((c) => {
            const cur = latest.scores[c.key] ?? 0;
            const prev = previous?.scores?.[c.key];
            const d = prev !== undefined ? cur - prev : null;
            return (
              <div key={c.key}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-neutral-200">{c.label}</span>
                  <span className="text-neutral-400 inline-flex items-center gap-1">
                    {cur}/100
                    {d !== null && diffIcon(d)}
                  </span>
                </div>
                <div className="score-bar"><div style={{ width: `${cur}%` }} /></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly trend + challenge */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6" data-testid="panel-monthly-block">
        <div className="lg:col-span-2 glass p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Aylık Gelişim Trendi</div>
            <div className="text-xs text-neutral-500">{monthly.length} ay verisi</div>
          </div>
          {monthly.length >= 2 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={monthly} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#1a1a1a" strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fill: "#a3a3a3", fontSize: 11 }} stroke="#2a2a2a" />
                <YAxis domain={[0, 100]} tick={{ fill: "#a3a3a3", fontSize: 11 }} stroke="#2a2a2a" />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#fff" }} formatter={(v) => [`${v}/100`, "Ortalama"]} />
                <Line type="monotone" dataKey="avg" stroke="#ffffff" strokeWidth={2} dot={{ fill: "#fff", r: 4, strokeWidth: 0 }} activeDot={{ r: 6, fill: "#fff" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-12 text-center text-neutral-500 text-sm">
              {monthly.length === 1
                ? "İlk ayını başlattın. Bir sonraki ay testi tekrar çözdüğünde gelişim trendini göreceksin."
                : "Henüz veri yok."}
            </div>
          )}
        </div>

        <div className="glass p-6 flex flex-col justify-between" data-testid="panel-challenge-card">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-neutral-500">
              <Award size={14} /> Aylık Challenge
            </div>
            {challengeMet ? (
              <>
                <div className="mt-4 text-2xl font-semibold text-white">Geçildi ✓</div>
                <p className="mt-3 text-sm text-neutral-300 leading-relaxed">
                  Geçen aya göre <span className="text-white font-semibold">+{monthDelta}</span> puan ilerleme. Aylık <span className="text-white">+2 puan</span> hedefini aştın.
                </p>
                <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 text-xs bg-white/10">
                  <Award size={12} /> Aylık Gelişim Rozeti
                </div>
              </>
            ) : monthDelta !== null ? (
              <>
                <div className="mt-4 text-2xl font-semibold text-white">Devam Ediyor</div>
                <p className="mt-3 text-sm text-neutral-300 leading-relaxed">
                  Geçen aya göre <span className="text-white font-semibold">{monthDelta >= 0 ? `+${monthDelta}` : monthDelta}</span> puan. Bu ay hedefin: <span className="text-white">+2 puan</span>.
                </p>
                <div className="mt-5 score-bar"><div style={{ width: `${Math.min(100, Math.max(0, ((monthDelta + 2) / 4) * 100))}%` }} /></div>
              </>
            ) : (
              <>
                <div className="mt-4 text-2xl font-semibold text-white">Hedef: +2 puan</div>
                <p className="mt-3 text-sm text-neutral-300 leading-relaxed">
                  Bir sonraki ay testi tekrar çöz; geçen aya göre <span className="text-white">+2 puan</span> ilerlersen rozeti kazanırsın.
                </p>
              </>
            )}
          </div>
          <button onClick={() => nav("/test")} className="btn-primary mt-6 inline-flex items-center justify-center gap-2 text-sm" data-testid="challenge-retake">
            <Target size={14} /> Şimdi Tekrar Çöz
          </button>
        </div>
      </div>

      {/* History list */}
      <div className="glass mt-6 p-6" data-testid="panel-history">
        <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-5">Geçmiş Testlerin ({subs.length})</div>
        <div className="space-y-3">
          {subs.map((s, i) => {
            const prevS = subs[i + 1];
            const dl = prevS ? Math.round(s.general_score - prevS.general_score) : 0;
            return (
              <div key={s.id} className="flex flex-wrap items-center gap-4 border border-white/5 rounded-xl px-4 py-3">
                <div className="text-xs text-neutral-500 w-20">#{subs.length - i}</div>
                <div className="text-2xl font-semibold text-white w-20">{s.general_score}<span className="text-xs text-neutral-500">/100</span></div>
                {prevS ? <div className="inline-flex items-center gap-1 text-xs text-neutral-400">{diffIcon(dl)} {dl > 0 ? `+${dl}` : dl}</div> : <div className="text-xs text-neutral-600">İlk test</div>}
                <div className="text-sm text-neutral-300 flex-1 min-w-[200px]">
                  Düşük: <span className="text-neutral-100">{(s.development_areas || []).slice(0, 3).join(", ") || "—"}</span>
                </div>
                <div className="text-xs text-neutral-500">{fmt(s.completed_at)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
